import React, { Dispatch, ReactElement, useMemo } from 'react';
import {
  useListReports,
  useListSprints,
  useListProducts,
  useRemoveReport,
  ProductStatus,
  ReportEntity,
  SprintEntity,
  useCreateReport,
  useUpdateReport,
  useFinishedDays,
  useSetDayFinished,
} from '@data';
import { daysInSprint, FULL_WORKDAY_MINUTES, tabs } from '@app/constants/app';
import {
  useToggleState,
  addDays,
  fullDate,
  isWeekendDate,
  toFixedHours,
  monthAndDate,
  year,
  getCurrentSprint,
  sprintPageRange,
  sprintYearPages,
} from '@app/utils';
import { useOrganizationId } from '@app/utils/hooks';
import { useStore } from '@data/storages';
import { useLocation, useParams } from 'react-router-dom';
import { TabsType } from '@app/utils/types';
import { useMembership, useSendMembershipReminder } from '@data/hooks/membership';
import { Trans, useTranslation } from 'react-i18next';
import { membersReportingListPath } from '@app/constants/url';
import { DateRangeFilter, ClearFilterButton, Pagination, Switch, CollapseIn } from '@app/components/core';
import { useBackNavigation } from '@app/components/core/useBackNavigation';
import Accordion from '@app/components/core/accordion';
import { Loader } from '@ui';
import SprintDayReport from '@app/components/shared/sprintDayReport';
import PendingAbsencesProvider from '@app/components/shared/sprintDayReport/PendingAbsencesProvider';
import { onlyProducts } from '@data/utils/transform-user-reports-data';
import { ChevronLeft } from 'lucide-react';
import ReportModal from '@app/components/shared/reportModal';
import RemoveReportModal from '@app/components/shared/removeReportModal';
import ReportIncidentDialog from '@app/components/shared/phillReport/ReportIncidentDialog';
import { cardClass } from '@app/constants/styles';
import SprintHeader from './SprintHeader';

interface MemberReportingProps {
  activeTab: TabsType;
  setActiveTab: Dispatch<TabsType>;
}

function MemberReportingLoaded({
  activeTab,
  setActiveTab,
  organizationId,
}: MemberReportingProps & { organizationId: string }): ReactElement {
  const [showWeekend, toggleShowWeekend] = useToggleState(false);
  const [expandAll, toggleExpandAll] = useToggleState(false);
  const [productIds, setProductIds] = React.useState<string[]>([]);
  // Past-sprints controls: a From/To window and a page index into the (filtered) sprint history.
  const [from, setFrom] = React.useState('');
  const [to, setTo] = React.useState('');
  const [page, setPage] = React.useState(0);

  // One fixed "now" for the whole screen. A fresh `new Date()` per render would land in the sprint
  // and report query keys, so every render would start a new request and no request would ever be
  // the current one long enough for its data to stick — the page would spin forever.
  const todayDate = useMemo(() => new Date(), []);

  const isPast = activeTab === tabs.pastSprints;
  const rangeActive = Boolean(from && to);
  const fromMissing = Boolean(to) && !from;
  const toMissing = Boolean(from) && !to;

  // Any change to filter/scope/tab jumps back to the first (most recent) page.
  React.useEffect(() => {
    setPage(0);
  }, [from, to, productIds, activeTab]);

  const { memberId } = useParams<{ memberId: string }>();
  const { state: locationState } = useLocation();

  const { data: membership, isLoading: membershipIsLoading } = useMembership(memberId!);

  const userId = membership?.user?.id || '';

  const { data: products } = useListProducts(organizationId, {
    statuses: [ProductStatus.active, ProductStatus.support],
  });

  // The current sprint anchors both tabs — its start marks where "past" ends.
  const rangeForCurrentSprint = React.useMemo(
    () => ({ from: todayDate, to: addDays(todayDate, daysInSprint - 1) }),
    [todayDate],
  );
  const { data: currentSprintList } = useListSprints(rangeForCurrentSprint);
  // Newest first, so on a changeover day the sprint that just started wins, as in the members list.
  const currentSprint = React.useMemo(
    () =>
      getCurrentSprint(
        currentSprintList?.items
          .slice()
          .sort((a, b) => new Date(b.startDate).getTime() - new Date(a.startDate).getTime()),
      ),
    [currentSprintList],
  );

  // The full past-sprint universe (newest first), narrowed to the filter window and cut off before
  // the current sprint. Fetched with a generous limit so the pager can address the whole history.
  const rangeForPastSprints = React.useMemo(
    () => ({
      from: rangeActive ? new Date(from) : undefined,
      to: rangeActive ? new Date(`${to}T23:59:59.999`) : todayDate,
      limit: 500,
    }),
    [rangeActive, from, to, todayDate],
  );
  const { data: pastSprintsData } = useListSprints(rangeForPastSprints);
  const pastSprints = React.useMemo(() => {
    const currentStart = currentSprint?.startDate ? new Date(currentSprint.startDate).getTime() : undefined;
    return (
      (pastSprintsData?.items ?? [])
        // By identity as well as date: the sprint just finished ends the instant the live one starts.
        .filter(
          sprint =>
            sprint.id !== currentSprint?.id &&
            (currentStart == null || new Date(sprint.startDate).getTime() < currentStart),
        )
        .sort((a, b) => new Date(b.startDate).getTime() - new Date(a.startDate).getTime())
    );
  }, [pastSprintsData, currentSprint]);

  // One page per calendar year, so a page never straddles New Year (see sprintYearPages).
  const yearPages = React.useMemo(() => sprintYearPages(pastSprints), [pastSprints]);
  const pageCount = Math.max(1, yearPages.length);
  const currentPage = Math.min(page, pageCount - 1);
  const pageSprints = React.useMemo(() => yearPages[currentPage]?.sprints ?? [], [yearPages, currentPage]);

  // Arriving from a sprint's row in the members list, that sprint is the one being asked about: turn the
  // pager to the year holding it and let its own accordion start open. Applied once, on arrival — paging
  // after that is the reader's business.
  const focusSprintId = (locationState as { focusSprintId?: string } | null)?.focusSprintId;

  // The list shows current and past sprints together, so the tab follows the sprint that was clicked.
  const focusTabApplied = React.useRef(false);
  React.useEffect(() => {
    // Waits on the lookup, not a sprint: on a day between sprints there is none, and past ones still count.
    if (focusTabApplied.current || !focusSprintId || !currentSprintList) return;
    if (focusSprintId === currentSprint?.id) setActiveTab(tabs.currentSprint);
    else if (pastSprints.some(sprint => sprint.id === focusSprintId)) setActiveTab(tabs.pastSprints);
    else return;
    focusTabApplied.current = true;
  }, [focusSprintId, currentSprintList, currentSprint, pastSprints, setActiveTab]);

  const focusApplied = React.useRef(false);
  React.useEffect(() => {
    if (focusApplied.current || !focusSprintId || !isPast || !yearPages.length) return;
    const index = yearPages.findIndex(yearPage => yearPage.sprints.some(sprint => sprint.id === focusSprintId));
    if (index === -1) return;
    focusApplied.current = true;
    setPage(index);
  }, [focusSprintId, isPast, yearPages]);

  // One request covers the whole page of sprints; the days come back flat and are regrouped per
  // sprint below (see `pageSprints.map`).
  const pageRange = React.useMemo(() => sprintPageRange(pageSprints), [pageSprints]);

  // Every product, since whether a day is full depends on the whole day; the product filter is applied below.
  const paramsForReports = React.useMemo(
    () =>
      isPast
        ? { from: pageRange?.from, to: pageRange?.to, productIds: [] }
        : { from: currentSprint?.startDate, to: todayDate, productIds: [] },
    [isPast, pageRange, currentSprint, todayDate],
  );

  const reportsEnabled = !!userId && (isPast ? !!pageRange : !!currentSprint);

  // Always fetch the full week (`hideWeekend: false`), like the Reporting tab does. Weekend rows then
  // stay mounted and the toggle only collapses them (see renderDays), so it animates instead of
  // changing the query key and swapping the list out from under you.
  const { data: reports, isFetching } = useListReports(organizationId, userId, paramsForReports, reportsEnabled, false);

  // A year of sprints is a long page and the one opened for you can sit well below the fold, so bring it
  // up — once its days are in, so the accordion is at its final height before anything moves.
  const focusScrolled = React.useRef(false);
  React.useEffect(() => {
    if (focusScrolled.current || !focusSprintId || !reports) return;
    const element = document.getElementById(`sprint-${focusSprintId}`);
    if (!element) return;
    focusScrolled.current = true;
    element.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, [focusSprintId, reports]);

  // The window on show, so a day this member settled is not drawn as one still owed.
  const finishedWindow = React.useMemo(
    () => (isPast ? pageRange : currentSprint && { from: currentSprint.startDate, to: todayDate }),
    [isPast, pageRange, currentSprint, todayDate],
  );
  const { data: finished } = useFinishedDays(organizationId, userId, finishedWindow, !!finishedWindow);
  const finishedDays = React.useMemo(() => new Set(finished?.days ?? []), [finished]);

  const { tokenData } = useStore();
  const isOwnReporting = !!userId && tokenData?.userId === userId;
  const { mutate: setDayFinished } = useSetDayFinished(organizationId, userId);

  const { mutate: removeReport } = useRemoveReport(organizationId!, userId, paramsForReports, false);

  const { mutate: sendMembershipReminder, isPending: sendMembershipReminderIsLoading } = useSendMembershipReminder(
    organizationId!,
  );

  const { t } = useTranslation();
  const { label: backLabel, goBack } = useBackNavigation(
    membersReportingListPath(organizationId),
    t('actions.backToMembersReporting'),
  );

  const [isAddFormOpen, toggleAddForm] = useToggleState(false);
  const [isDeleteFormOpen, toggleDeleteForm] = useToggleState(false);
  const [reportToEdit, setReportToEdit] = React.useState<ReportEntity | null>(null);
  const [selectedReport, setReportSelected] = React.useState<ReportEntity | null>(null);
  const [workDayToAdd, setWorkDayToAdd] = React.useState<string | undefined>();

  const showEditForm = report => {
    setReportToEdit(report);
  };
  const closeEditForm = () => {
    setReportToEdit(null);
  };

  const showAddForm = (workDay: string) => {
    setWorkDayToAdd(workDay);
    toggleAddForm();
  };
  const closeAddForm = () => {
    toggleAddForm();
    setWorkDayToAdd(undefined);
  };

  const showDeleteForm = report => {
    setReportSelected(report);
    toggleDeleteForm();
  };
  const closeDeleteForm = () => {
    toggleDeleteForm();
    setReportSelected(null);
  };

  const handleDeleting = () => {
    if (selectedReport) {
      removeReport(selectedReport?.id);
    }
    closeDeleteForm();
  };

  const { mutate: createReport, mutateAsync: createReportAsync } = useCreateReport(
    userId,
    organizationId!,
    paramsForReports,
    false,
  );

  const handleCreateReport = (report: ReportEntity) => {
    createReport(report);
    closeAddForm();
  };

  const { mutate: updateReport, mutateAsync: updateReportAsync } = useUpdateReport(
    organizationId!,
    userId,
    paramsForReports,
    false,
  );

  const handleUpdateReport = (report: ReportEntity) => {
    updateReport(report);
    closeEditForm();
  };

  const handleSendMembershipReminder = () => {
    sendMembershipReminder({ id: membership?.id });
  };

  // All days for the active request, flattened; the past tab regroups these per sprint by date.
  const allWorkdays = reports?.pages?.flatMap(reportPage => reportPage.workDaysData) ?? [];
  const fullDays = new Set(allWorkdays.filter(day => day.totalMinutes >= FULL_WORKDAY_MINUTES).map(day => day.date));
  const workdays = onlyProducts(allWorkdays, productIds);

  // The sprints and their days have to reach the view together. `pageSprints` flips the moment a year is
  // clicked, while its days are still in flight and keepPreviousData is still serving the year before —
  // so the new sprints would render against the wrong days, as rows of zero hours. Recomputing only when
  // the days change holds the previous year until its replacement is ready, and is what the fade keys on.
  const shownPast = React.useMemo(
    () => ({ key: pageSprints[0]?.id ?? currentPage, sprints: pageSprints, workdays: allWorkdays }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [reports?.pages],
  );

  if (membershipIsLoading) {
    return <Loader />;
  }

  const dayReport = (item: (typeof workdays)[number]): ReactElement => (
    <SprintDayReport
      workDay={item}
      todayDate={todayDate}
      showAddForm={showAddForm}
      showEditForm={showEditForm}
      showDeleteForm={showDeleteForm}
      expandAll={expandAll}
      isFinished={finishedDays.has(item.date)}
      isFull={fullDays.has(item.date)}
      // Yours alone to settle: a manager reads this page too, and the API refuses the mark from anybody else.
      onSetFinished={
        isOwnReporting ? isFinished => setDayFinished({ workday: item.date, finished: isFinished }) : undefined
      }
    />
  );

  // The toggle only hides weekend days nobody worked: a weekend that was actually reported on always
  // shows, since hiding logged time would misrepresent the sprint. Those hideable rows stay mounted
  // and collapse in/out via CollapseIn so the toggle animates both ways; the wrapper carries the row
  // gap so that collapses with it (a margin on the row itself wouldn't).
  const renderDays = (days: typeof workdays): ReactElement[] =>
    days.map(item =>
      isWeekendDate(item.date) && item.reports.length === 0 ? (
        <CollapseIn key={item.date} show={showWeekend}>
          <div className="pb-5">{dayReport(item)}</div>
        </CollapseIn>
      ) : (
        React.cloneElement(dayReport(item), { key: item.date })
      ),
    );

  const switchesRow = (
    <div className="my-2 mb-6 flex items-center justify-end gap-4">
      <Switch value={expandAll} onChange={toggleExpandAll} text={t('titles.expandAll')} />
      <Switch value={showWeekend} onChange={toggleShowWeekend} text={t('titles.showWeekend')} />
    </div>
  );

  const sprintSummary = (sprint: SprintEntity, totalHours: number): ReactElement => (
    <div className="flex w-full min-w-0 flex-wrap items-center justify-between gap-2 sm:[&>span:last-child]:ml-auto">
      <h2 className="mr-3 text-base font-bold text-foreground">
        {t('sprint')} {sprint.number}
      </h2>
      <span className="text-base text-neutral-600 dark:text-muted-foreground">
        {monthAndDate(sprint.startDate)} – {monthAndDate(sprint.endDate)}
        {isPast && <>, {year(sprint.endDate)}</>}
      </span>
      <span className="text-foreground">
        <Trans
          defaults={t('sprintTotalHours', { totalHours })}
          components={{ bold: <strong className="ms-1.5 font-bold tabular-nums" /> }}
        />
      </span>
    </div>
  );

  const skeleton = (
    <div className="w-full">
      {[...new Array(10)].map((_, i) => (
        <div key={Number(i)} className="not-last:mb-5 overflow-hidden rounded-sm bg-card">
          <div className="h-10 border-b border-border bg-background" />
        </div>
      ))}
    </div>
  );

  let body: ReactElement;
  if (isPast) {
    const pastLoading = !pastSprintsData || (!!pageRange && !reports);
    let pastContent: ReactElement;
    if (pastLoading) {
      pastContent = skeleton;
    } else if (pageSprints.length === 0) {
      pastContent = <p className="py-10 text-center text-sm text-muted-foreground">{t('noEntriesFound')}</p>;
    } else {
      pastContent = (
        // Keyed on `shownPast`, which only advances once the year's days have arrived, so the fade runs
        // when the entries render rather than on the click while they are still loading.
        <div
          key={shownPast.key}
          className={`w-full duration-200 animate-in fade-in-0 ${isFetching ? 'opacity-60 transition-opacity' : ''}`}
        >
          {shownPast.sprints.map(sprint => {
            const startKey = fullDate(sprint.startDate);
            const endKey = fullDate(sprint.endDate);
            const days = onlyProducts(shownPast.workdays, productIds).filter(
              day => day.date >= startKey && day.date <= endKey,
            );
            const totalHours = toFixedHours(days.reduce((sum, day) => sum + day.totalMinutes, 0) / 60);
            return (
              <Accordion
                key={sprint.id}
                id={`sprint-${sprint.id}`}
                isOpen={sprint.id === focusSprintId}
                containerClassName="not-last:mb-4 rounded-sm bg-card px-6"
                className="!px-0 !py-6"
                titleContent={
                  <div className="flex w-full flex-wrap items-center">{sprintSummary(sprint, totalHours)}</div>
                }
              >
                {/* The card only pads its sides, so without this the last day's row ended exactly on the
                    card's bottom edge — its rounded corners shaved off, reading as content cut short.
                    Matches the sprint accordion on the personal Reporting page. */}
                <div className="pb-6">
                  {switchesRow}
                  {renderDays(days)}
                </div>
              </Accordion>
            );
          })}
        </div>
      );
    }
    body = (
      <>
        {pastContent}
        {!pastLoading && pageSprints.length > 0 && (
          <Pagination
            className="mt-8"
            page={currentPage}
            pageCount={pageCount}
            onChange={setPage}
            labels={yearPages.map(yearPage => String(yearPage.year))}
            reversed
          />
        )}
      </>
    );
  } else if (!currentSprint || (reportsEnabled && !reports)) {
    body = skeleton;
  } else {
    const totalHours = toFixedHours(workdays.reduce((sum, day) => sum + day.totalMinutes, 0) / 60);
    body = (
      <div className={`mb-4 ${cardClass}`}>
        <div className="mb-7">{sprintSummary(currentSprint, totalHours)}</div>
        {switchesRow}
        {renderDays(workdays)}
      </div>
    );
  }

  return (
    <div className="w-full">
      <button
        type="button"
        onClick={goBack}
        className="mb-3.5 flex w-fit items-center gap-1 text-sm font-medium text-muted-foreground transition-colors hover:text-foreground"
      >
        <ChevronLeft className="size-4" />
        {backLabel}
      </button>

      <SprintHeader
        activeTab={activeTab}
        setActiveTab={setActiveTab}
        productIds={productIds}
        setProductIds={setProductIds}
        products={products}
        handleSendMembershipReminder={handleSendMembershipReminder}
        sendMembershipReminderIsLoading={sendMembershipReminderIsLoading}
        user={membership?.user}
        dateFilter={
          isPast ? (
            <div className="flex shrink-0 flex-nowrap items-end gap-3">
              <DateRangeFilter
                idPrefix="member-reports"
                from={from}
                to={to}
                onFromChange={setFrom}
                onToChange={setTo}
                fromMissing={fromMissing}
                toMissing={toMissing}
                hideClear
              />
              {from && to && (
                <ClearFilterButton
                  onClick={() => {
                    setFrom('');
                    setTo('');
                  }}
                />
              )}
            </div>
          ) : undefined
        }
      />

      <div className="w-full">{body}</div>

      <ReportModal
        title={t('titles.editEntry')}
        show={!!reportToEdit && !reportToEdit.incidentId}
        onHide={closeEditForm}
        initialData={reportToEdit}
        onSubmit={handleUpdateReport}
        disabledDays={{ after: new Date() }}
        userId={userId}
      />

      <ReportModal
        title={t('titles.addEntry')}
        show={isAddFormOpen}
        onHide={closeAddForm}
        onSubmit={handleCreateReport}
        onSubmitIncident={async values => {
          await createReportAsync(values);
          closeAddForm();
        }}
        disabledDays={{ after: new Date() }}
        date={workDayToAdd}
        userId={userId}
      />

      {/* Incident hours are corrected in the form they were reported in: a day and two clock times,
          not a number of hours. The same write either way, so one ledger. */}
      <ReportIncidentDialog
        show={!!reportToEdit?.incidentId}
        onHide={closeEditForm}
        userId={userId}
        report={reportToEdit?.incidentId ? reportToEdit : null}
        onSubmit={values => updateReportAsync(values)}
      />

      <RemoveReportModal show={isDeleteFormOpen} onHide={closeDeleteForm} onRemove={handleDeleting} />
    </div>
  );
}

const MemberReporting: React.FC<MemberReportingProps> = (props): ReactElement => {
  const organizationId = useOrganizationId();
  const { memberId } = useParams<{ memberId: string }>();
  const { data: membership } = useMembership(memberId!);

  if (!organizationId) {
    return <Loader />;
  }

  return (
    <PendingAbsencesProvider organizationId={organizationId} userId={membership?.user?.id}>
      <MemberReportingLoaded {...props} organizationId={organizationId} />
    </PendingAbsencesProvider>
  );
};

export default MemberReporting;
